import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { useEffect } from 'react';
import './index.css';

import { AuthProvider, useAuth } from './context/AuthContext';
import { ModalProvider } from './context/ModalContext';
import { WalletLockProvider } from './context/WalletLockContext';
import Navbar from './components/Navbar';
import Landing from './pages/Landing';
import CompleteProfile from './pages/CompleteProfile';
import Tournaments from './pages/Tournaments';
import TournamentDetail from './pages/TournamentDetail';
import WalletPage from './pages/WalletPage';
import MyTournaments from './pages/MyTournaments';
import Leaderboard from './pages/Leaderboard';
import Profile from './pages/Profile';
import Support from './pages/Support';
import Notifications from './pages/Notifications';

// Protects routes that require authentication AND a completed profile
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading, needsProfileSetup } = useAuth();
  if (loading) return <div className="page-wrapper" style={{ textAlign: 'center', paddingTop: 100 }}>Loading...</div>;
  if (!user) return <Navigate to="/" replace />;
  if (needsProfileSetup) return <Navigate to="/complete-profile" replace />;
  return <>{children}</>;
}

// Scrolls to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function AppContent() {
  const location = useLocation();
  const { user, loading, needsProfileSetup } = useAuth();
  const isAuthPage = location.pathname === '/complete-profile';

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', color: 'var(--text-muted)' }}>
        <span style={{ fontSize: '1.5rem' }}>🎮 Loading TournaHub...</span>
      </div>
    );
  }

  // If user has Auth account but no profile, redirect to /complete-profile
  if (user && needsProfileSetup && location.pathname !== '/complete-profile') {
    return <Navigate to="/complete-profile" replace />;
  }

  return (
    <>
      {!isAuthPage && <Navbar isLoggedIn={!!user} />}
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/complete-profile" element={
          user && needsProfileSetup ? <CompleteProfile /> : <Navigate to="/" replace />
        } />
        {/* Legacy auth routes → redirect to home */}
        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route path="/register" element={<Navigate to="/" replace />} />
        <Route path="/tournaments" element={<Tournaments />} />
        <Route path="/tournaments/:id" element={<TournamentDetail />} />
        <Route path="/wallet" element={<ProtectedRoute><WalletPage /></ProtectedRoute>} />
        <Route path="/my-tournaments" element={<ProtectedRoute><MyTournaments /></ProtectedRoute>} />
        <Route path="/leaderboard" element={<Leaderboard />} />
        <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
        <Route path="/support" element={<Support />} />
        <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
      </Routes>
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <AuthProvider>
        <ModalProvider>
          <WalletLockProvider>
            <AppContent />
          </WalletLockProvider>
        </ModalProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
